import { InfoIcon } from '@chakra-ui/icons';
import {
  Box,
  Card,
  Flex,
  HStack,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import OfficeNotesIcon from '../../../assets/icons/office-notes.svg';
import ProvidersIcon from '../../../assets/icons/provider.svg';
import CustomFieldBadge from '../../../components/customfield-badge/CustomFieldBadge';
import CustomFieldLabel from '../../../components/customfield-label/CustomFieldLabel';
import DirectionsButton from '../../../components/directions-btn/DirectionsButton';
import ExternalAffordance from '../../../components/external-indicator/ExternalAffordance';
import PracticeStatusBadge from '../../../components/practice-status-badge/PracticeStatusBadge';
import { PracticeClosure } from '../../../types/closures/types';
import {
  CustomFields,
  Practice,
} from '../../../types/practices/types';
import {
  getCustomFieldValue,
  getLabImagingDisplay,
  getLabImagingValue,
} from '../../../utils/customFieldUtils';
import { isExternalPractice } from '../../../utils/externalEntity';
import { getFluShotsStatus } from '../../../utils/fluShotsUtils';
import { constructPracticeAddress } from '../../../utils/practiceUtils';
import PracticeStatusBox from './PracticeStatusBox';

interface PracticeItemProps {
  practice: Practice;
  onClick?: () => void;
  officeHoursLoading?: boolean;
  clinicClosures?: PracticeClosure[] | null;
  isClosuresLoading?: boolean;
}
const getMetadataCount = (
  customField: CustomFields | undefined,
  fieldName: keyof CustomFields,
): number => {
  const fieldValues = customField?.[fieldName];
  return Array.isArray(fieldValues) ? fieldValues.length : 0;
};

export const PracticeItem: React.FC<PracticeItemProps> = ({
  practice,
  onClick,
  officeHoursLoading,
  clinicClosures,
  isClosuresLoading,
}) => {
  const navigate = useNavigate();
  const [address, setAddress] = useState<string>('');
  const externalTablespace = (
    practice as Practice & { externalTablespace?: string | null }
  ).externalTablespace;
  const isPracticeExternal = isExternalPractice(practice);

  const dailyAlertsCount = getMetadataCount(
    practice.customFields,
    'Daily-Alert',
  );
  const officeNotesCount = getMetadataCount(
    practice.customFields,
    'Office-Notes',
  );

  useEffect(() => {
    if (practice) {
      const completeAddress = constructPracticeAddress(practice);
      setAddress(completeAddress);
    }
  }, [practice]);

  // Construct Google Maps URL only if we have a valid address
  const googleMapsUrl = address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        address,
      )}`
    : '';
  return (
    <Card
      padding="1rem"
      borderRadius={'0.5rem'}
      borderColor={'border.subtle'}
      variant={'outline'}
    >
      <>
        {/* Header Section with Location, Name, Status, and Summary Icons */}
        <Flex
          justifyContent={'space-between'}
          alignItems={'start'}
          mb={'0.75rem'}
        >
          <VStack align={'left'} spacing="0.25rem">
            {/* Location and ID */}
            <Tooltip label={'Market Name'}>
              <Text
                color={'gray.600'}
                fontSize={'0.75rem'}
                fontWeight={'400'}
                lineHeight={'1rem'}
                onClick={() =>
                  navigate(`/markets/${practice?.marketId}`)
                }
                cursor="pointer"
                _hover={{ color: 'blue.500' }}
              >
                {practice.market}
              </Text>
            </Tooltip>

            {/* Name */}
            <Flex
              gap="0.5rem"
              flexWrap="wrap"
              alignItems="flex-start"
            >
              <Text
                fontSize={'1.25rem'}
                fontWeight={'700'}
                lineHeight={'1.5rem'}
                color={'text.primary'}
                onClick={onClick}
                _hover={{
                  color: 'text.brandLight',
                }}
                cursor="pointer"
                flex="1"
                minWidth="12rem"
                whiteSpace="normal"
                wordBreak="break-word"
              >
                {practice.name ? practice.name : 'N/A'}
              </Text>
              {isPracticeExternal && (
                <ExternalAffordance entityType="practice" size="md" />
              )}
              {externalTablespace && (
                <CustomFieldLabel
                  customField="Tablespace"
                  fieldValue={externalTablespace}
                  fieldValueBgColor="background.green100"
                  fieldValueTextColor="green.800"
                />
              )}
              {/* Status Badge */}
              {practice && !isPracticeExternal && (
                <PracticeStatusBadge
                  active={practice.active ?? true}
                  practiceOnPac={practice.practiceOnPac}
                  practiceHospitalFollowupPac={practice.practiceHospitalFollowupPac}
                  practiceSickCrossCoveragePac={practice.practiceSickCrossCoveragePac}
                />
              )}{' '}
            </Flex>
          </VStack>

          {/* Summary Icons - Top Right */}
          <Flex gap={'1rem'}>
            <Tooltip label="Daily Alerts">
              <Flex
                alignItems={'center'}
                justifyContent={'center'}
                gap={'0.25rem'}
              >
                <InfoIcon
                  color={'red.500'}
                  width={'1rem'}
                  height={'1rem'}
                />
                <Text
                  lineHeight={'150%'}
                  fontSize={'0.875rem'}
                  fontWeight={'700'}
                  color="text.primary"
                >
                  {dailyAlertsCount}
                </Text>
              </Flex>
            </Tooltip>

            <Tooltip label={'Office Notes'}>
              <Flex alignItems={'center'} gap={'0.25rem'}>
                <img
                  src={OfficeNotesIcon}
                  alt="Office Notes"
                  width="16"
                  height="16"
                />
                <Text
                  fontSize={'0.875rem'}
                  fontWeight={'700'}
                  lineHeight={'150%'}
                  color="text.primary"
                >
                  {officeNotesCount}
                </Text>
              </Flex>
            </Tooltip>

            <Tooltip label={'Providers'}>
              <Flex alignItems={'center'} gap={'0.25rem'}>
                <img
                  src={ProvidersIcon}
                  alt="Providers"
                  width="16"
                  height="16"
                />
                <Text
                  lineHeight={'150%'}
                  fontSize={'0.875rem'}
                  fontWeight={'700'}
                  color="text.primary"
                >
                  {practice?.providerCount || 0}
                </Text>
              </Flex>
            </Tooltip>
          </Flex>
        </Flex>
        <Flex maxWidth="100%" wrap="wrap" gap="1rem">
          {/* Left Section */}
          <VStack gap="0.25rem" align="start" flex={1} minWidth={0}>
            {getCustomFieldValue(
              practice.customFields,
              'Backline',
            ) && (
              <CustomFieldBadge
                customField="Backline"
                fieldValue={getCustomFieldValue(
                  practice.customFields,
                  'Backline',
                )}
                bgColor="#FED7D7"
                titleColor="#9B2C2C"
                valueColor="#9B2C2C"
              />
            )}
            <HStack
              spacing="0.5rem"
              wrap="nowrap"
              alignItems="center"
              minWidth={0}
            >
              {getCustomFieldValue(
                practice.customFields,
                'Manager-Line',
              ) && (
                <CustomFieldBadge
                  customField="Escalation"
                  fieldValue={getCustomFieldValue(
                    practice.customFields,
                    'Manager-Line',
                  )}
                  bgColor="background.orange200"
                />
              )}
              {/* {(getCustomFieldValue(practice.customFields, 'Manager-Name') || getCustomFieldValue(practice.customFields, 'Manager-Email')) && (
                <VStack align="start" spacing={0} maxWidth="180px">
                  {getCustomFieldValue(practice.customFields, 'Manager-Name') && (
                    <Tooltip label="Manager Name" hasArrow>
                      <Text
                        fontSize="0.75rem"
                        fontWeight="700"
                        color="text.primary"
                        whiteSpace="nowrap"
                        textOverflow="ellipsis"
                        overflow="hidden"
                        maxWidth="180px"
                      >
                        {getCustomFieldValue(practice.customFields, 'Manager-Name')}
                      </Text>
                    </Tooltip>
                  )}
                  {getCustomFieldValue(practice.customFields, 'Manager-Email') && (
                    <Tooltip label="Manager Email" hasArrow>
                      <Text
                        fontSize="0.75rem"
                        fontWeight="400"
                        color="text.orange600"
                        whiteSpace="nowrap"
                        textOverflow="ellipsis"
                        overflow="hidden"
                        maxWidth="180px"
                      >
                        {getCustomFieldValue(practice.customFields, 'Manager-Email')}
                      </Text>
                    </Tooltip>
                  )}
                </VStack>
              )} */}
            </HStack>
            {getCustomFieldValue(
              practice.customFields,
              'Answering Service',
            ) && (
              <CustomFieldBadge
                customField="Answering Service"
                fieldValue={getCustomFieldValue(
                  practice.customFields,
                  'Answering Service',
                )}
                bgColor="background.cyan100"
              />
            )}
            {getCustomFieldValue(practice.customFields, 'Phone') && (
              <CustomFieldBadge
                customField="Phone"
                fieldValue={getCustomFieldValue(
                  practice.customFields,
                  'Phone',
                )}
                bgColor="background.gray50"
              />
            )}
            {getCustomFieldValue(practice.customFields, 'Fax') && (
              <CustomFieldBadge
                customField="Fax"
                fieldValue={getCustomFieldValue(
                  practice.customFields,
                  'Fax',
                )}
                bgColor={'background.gray50'}
              />
            )}
            {getCustomFieldValue(
              practice.customFields,
              'Group-TID',
            ) && (
              <CustomFieldBadge
                customField="Tax ID"
                fieldValue={getCustomFieldValue(
                  practice.customFields,
                  'Group-TID',
                )}
                bgColor={'background.gray50'}
              />
            )}
          </VStack>

          {/* Right Section */}
          <Box flex={2} minWidth={0}>
            <Box ml="0.62rem">
              <PracticeStatusBox
                timezone={practice.timezone || ''}
                state={practice.state || ''}
                unFormattedOfficeHours={practice.office_hours}
                isLoading={officeHoursLoading}
                hideMoreDetails={false}
                practiceId={practice.id}
                clinicClosures={clinicClosures}
                isClosuresLoading={isClosuresLoading}
              />
              <Flex
                mt="0.75rem"
                mb="0.5rem"
                gap="0.5rem"
                wrap="nowrap"
              >
                {address && (
                  <Text
                    fontSize="0.75rem"
                    fontWeight="400"
                    lineHeight="1.125rem"
                    color="text.secondary"
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                  >
                    {address}
                  </Text>
                )}
                {googleMapsUrl && (
                  <DirectionsButton googleMapsUrl={googleMapsUrl} />
                )}
              </Flex>
              <Flex wrap="wrap" gap="0.5rem" alignItems={'center'}>
                {practice?.customFields.minAge !== undefined &&
                  practice?.customFields.maxAge !== undefined && (
                    <CustomFieldLabel
                      customField="Age Range"
                      fieldValue={`${practice?.customFields.minAge} - ${practice?.customFields.maxAge}`}
                      fieldValueBgColor={`${
                        practice?.treatsAllAges
                          ? 'background.teal100'
                          : 'background.red100'
                      }`}
                    />
                  )}
                {(() => {
                  const fluShotsValue = getCustomFieldValue(
                    practice?.customFields,
                    'Flu-Shots',
                  );
                  const { displayText, bgColor } =
                    getFluShotsStatus(fluShotsValue);

                  return (
                    <CustomFieldLabel
                      customField="Flu shots"
                      fieldValue={displayText}
                      fieldValueBgColor={bgColor}
                    />
                  );
                })()}
                {(
                  [
                    ['Labs Hours', 'Labs-Drawn-Hours'],
                    ['Imaging Hours', 'Imaging-Available-Hours'],
                  ] as const
                ).map(([label, fieldName]) => {
                  const { displayText, bgColor } =
                    getLabImagingDisplay(
                      getLabImagingValue(
                        practice?.customFields,
                        fieldName,
                      ),
                    );
                  return (
                    <CustomFieldLabel
                      key={fieldName}
                      customField={label}
                      fieldValue={displayText}
                      fieldValueBgColor={bgColor}
                    />
                  );
                })}
              </Flex>
              <Flex
                mt="0.75rem"
                gap="0.5rem"
                wrap="nowrap"
                alignItems="center"
              >
                {getCustomFieldValue(
                  practice.customFields,
                  'Manager-Name',
                ) && (
                  <Tooltip label="Manager Name" hasArrow>
                    <Text
                      fontSize="0.75rem"
                      fontWeight="700"
                      color="text.primary"
                      whiteSpace="nowrap"
                      textOverflow="ellipsis"
                      overflow="hidden"
                    >
                      {getCustomFieldValue(
                        practice.customFields,
                        'Manager-Name',
                      )}
                    </Text>
                  </Tooltip>
                )}
                {getCustomFieldValue(
                  practice.customFields,
                  'Manager-Email',
                ) && (
                  <Tooltip label="Manager Email" hasArrow>
                    <Text
                      fontSize="0.75rem"
                      fontWeight="400"
                      color="text.orange600"
                      whiteSpace="nowrap"
                      textOverflow="ellipsis"
                      overflow="hidden"
                    >
                      {getCustomFieldValue(
                        practice.customFields,
                        'Manager-Email',
                      )}
                    </Text>
                  </Tooltip>
                )}
              </Flex>
            </Box>
          </Box>
        </Flex>
      </>
    </Card>
  );
};
